import { Check, Close, InfoOutlined, WarningAmber, WarningAmberRounded } from "@mui/icons-material"
import {
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  DialogActions,
  MenuItemProps,
  Alert,
  InputAdornment,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  DialogProps,
  Dialog,
  Paper,
  Tooltip as MuiTooltip,
  ButtonGroup,
  Grid,
} from "@mui/material"
import { Skeleton } from "@mui/material"
import { Box } from "@mui/material"
import { useStore } from "@nanostores/react"
import { useActiveAddress, useConnection } from "arweave-wallet-kit"
import React, { useCallback, FormEvent, useMemo, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"

import { Pool } from "@/api/pools-api"
import {
  checkSlippageOnFirstRebalance,
  deposit,
  PortfolioAgentInfo,
  PortfolioAgentParams,
  spawnPortfolioAgent,
} from "@/api/portfolio-agents-api"
import { TokenInfo } from "@/api/token-api"
import { IdBlock } from "@/components/IdBlock"
import { PortfolioAgentIcon } from "@/components/PortfolioAgentIcon"
import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountInCurrency } from "@/components/TokenAmountInCurrency"
import { TokenAvatar } from "@/components/TokenAvatar"
import {
  CreateAgentAnimation,
  CreateAgentStage,
} from "@/components/create-agent-anim/CreateAgentAnimation"
import ShineBorder from "@/components/shine-border"
import { useTokenInfo, useTokenInfos } from "@/hooks/use-token-info"
import { useBestPool } from "@/hooks/useBestPool"
import useHopperPrice from "@/hooks/useHopper"
import useTokenBalance from "@/hooks/useTokenBalance"
import { INPUT_DECIMALS, PAYMENT_TOKENS } from "@/settings"
import { $hotAgentsMap, $hotOwnersMap } from "@/stores/hot-agents-store"
import { $allPools } from "@/stores/pools-store"
import { truncateId } from "@/utils/data-utils"
import { formatNumberAuto, parseBigIntAsNumber, parseNumberAsBigInt } from "@/utils/format"
import { parseUnits } from "@/utils/math"
import { wait } from "@/utils/utils"

type SpawnAgentDialogProps = DialogProps & {
  prefill?: PortfolioAgentInfo
}

export function SpawnAgentDialog(props: SpawnAgentDialogProps) {
  const { prefill, ...rest } = props
  const activeAddress = useActiveAddress()
  const { connect } = useConnection()

  const pools = useStore($allPools)

  const [receipt, setReceipt] = React.useState<string | null>(null)
  const [backendError, setBackendError] = React.useState<Error | null>(null)

  const [quoteTokenId, setQuoteTokenId] = React.useState(
    PAYMENT_TOKENS[PAYMENT_TOKENS.length - 1].value,
  )
  const [portfolioTokenIds, setPortfolioTokenIds] = React.useState<string[]>([])
  const [deviation, setDeviation] = React.useState("10")
  const [slippage, setSlippage] = React.useState("")
  const [ignoreSlippage, setIgnoreSlippage] = React.useState(true)
  const [agentName, setAgentName] = React.useState("")
  const [investmentAmount, setInvestmentAmount] = React.useState("")
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [loading, setLoading] = React.useState(false)
  const [checkingLiquidity, setCheckingLiquidity] = React.useState(true)
  const [isLiquidityAcceptable, setIsLiquidityAcceptable] = React.useState(true)
  const [recommendedSlippage, setRecommendedSlippage] = React.useState(0)
  const [highlightedAsset, setHighlightedAsset] = React.useState("")

  // State to manage weights
  const [portfolioTokenWeights, setPortfolioTokenWeights] = React.useState<Record<string, string>>(
    {},
  )
  const [weightsPristine, setWeightsPristine] = React.useState(true)

  const portfolioTokenIdOptions = useMemo(() => {
    const filterdPools = pools.filter(
      (pool) =>
        (pool.quoteToken === quoteTokenId || pool.baseToken === quoteTokenId) &&
        pool.status !== "private",
    )

    const baseTokens = filterdPools.map((pool) =>
      pool.quoteToken === quoteTokenId ? pool.baseToken : pool.quoteToken,
    )

    // return [...new Set([...baseTokens, quoteTokenId])]
    return baseTokens
  }, [pools, quoteTokenId])

  const [portfolioTokenOptions] = useTokenInfos(portfolioTokenIdOptions)

  const [searchInput, setSearchInput] = useState("")

  const [tokenLiquidities, setTokenLiquidities] = useState<Record<string, number>>({})

  const [tokenLiquiditiesPool, setTokenLiquiditiesPool] = useState<
    Record<string, { pool: string; liquidity: number }>
  >({})

  const handleLiquidityFound = useCallback(
    (tokenId: string, liquidity: number, poolId?: string) => {
      setTokenLiquidities((prev) => ({ ...prev, [tokenId]: liquidity }))

      if (!poolId) return

      setTokenLiquiditiesPool((prev) => ({
        ...prev,
        [tokenId]: { pool: poolId, liquidity },
      }))
    },
    [],
  )

  const filteredTokenIdOptions = useMemo(() => {
    const tokenOptions = portfolioTokenOptions
      ?.filter((token) => {
        const query = searchInput.toLowerCase()
        if (!query || query === "") return true
        if (
          token.name?.toLowerCase().includes(query) ||
          token.ticker?.toLowerCase().includes(query) ||
          token.id?.toLowerCase().includes(query)
        )
          return true
        return false
      })
      ?.sort((a, b) => (tokenLiquidities[b.id] || 0) - (tokenLiquidities[a.id] || 0))

    // Filter tokenOptions for duplicates and pick only the one with highest tokenLiquidities
    const filteredTokenOptions = tokenOptions?.filter((token, index, self) => {
      const firstIndex = self.findIndex((t) => t.id === token.id)
      return firstIndex === index
    })

    return filteredTokenOptions
  }, [portfolioTokenOptions, searchInput, tokenLiquidities])

  const [quoteToken] = useTokenInfo(quoteTokenId)
  const [portfolioTokens] = useTokenInfos(portfolioTokenIds)
  const { data: balance } = useTokenBalance(quoteTokenId)

  // Initialize weights when portfolioTokenIds change
  useEffect(() => {
    if (weightsPristine) {
      const equalWeight =
        portfolioTokenIds.length > 0
          ? String(parseFloat((100 / portfolioTokenIds.length).toFixed(2)))
          : "0"
      const newWeights: Record<string, string> = {}
      portfolioTokenIds.forEach((tokenId) => {
        newWeights[tokenId] = equalWeight
      })
      // Make sure the weights equal to 100
      const totalAllocated = Object.values(newWeights).reduce(
        (sum, weight) => sum + Number(weight || 0),
        0,
      )
      if (totalAllocated < 100) {
        newWeights[portfolioTokenIds[0]] = (
          parseFloat(newWeights[portfolioTokenIds[0]]) +
          100 -
          totalAllocated
        ).toFixed(2)
      } else if (totalAllocated > 100) {
        newWeights[portfolioTokenIds[0]] = (
          parseFloat(newWeights[portfolioTokenIds[0]]) -
          totalAllocated +
          100
        ).toFixed(2)
      }

      setPortfolioTokenWeights(newWeights)
    } else {
      // Keep existing weights, remove weights for tokens that are no longer selected
      setPortfolioTokenWeights((prevWeights) => {
        const newWeights: Record<string, string> = {}
        portfolioTokenIds.forEach((tokenId) => {
          newWeights[tokenId] = prevWeights[tokenId] || ""
        })
        return newWeights
      })
    }
  }, [portfolioTokenIds, weightsPristine])

  const validationError = useMemo(() => {
    if (!quoteTokenId || !quoteToken) return "Quote token is required"
    if (portfolioTokenIds.length < 2) return "Select at least two portfolio tokens"
    if (deviation === "") return "Input a Deviation Percentage"
    if (Number(deviation) < 10) return "Deviation Percentage must be at least 10%"
    if (!ignoreSlippage && slippage === "") return "Input a Max Slippage"
    if (!ignoreSlippage && Number(slippage) < 5) return "Max Slippage must be at least 5%"
    if (investmentAmount === "") return "Input an Investment Amount"
    if (Number(investmentAmount) <= 0) return "Investment amount must be a positive number"
    if (Number(investmentAmount) > Number(parseBigIntAsNumber(balance, quoteToken.denomination)))
      return "Insufficient balance"

    // Check if any asset has weight <= 0
    const invalidWeights = Object.entries(portfolioTokenWeights).filter(
      ([_, weight]) => Number(weight) < 0.01,
    )
    if (invalidWeights.length > 0) return "No asset allocation should be smaller than 0.01%."

    const totalAllocated = Object.values(portfolioTokenWeights).reduce(
      (sum, weight) => sum + Number(weight || 0),
      0,
    )
    if (Math.abs(totalAllocated - 100) > 0.01) {
      return `Total allocation must be 100%, not ${totalAllocated.toFixed(2)}%.`
    }

    if (!isLiquidityAcceptable) {
      return "Liquidity check failed."
    }

    return "" // No validation errors
  }, [
    quoteTokenId,
    portfolioTokenIds,
    deviation,
    slippage,
    investmentAmount,
    portfolioTokenWeights,
    isLiquidityAcceptable,
    ignoreSlippage,
    balance,
    quoteToken,
  ])

  // agent creation stage
  const [agentStage, setAgentStage] = useState<CreateAgentStage>("done")

  const handleSubmit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      const newErrors: Record<string, string> = {}

      if (!quoteTokenId || !quoteToken) newErrors.quoteToken = "Quote token is required"
      if (portfolioTokenIds.length < 2)
        newErrors.portfolioTokens = "Select at least two portfolio tokens"
      if (Number(deviation) <= 0) newErrors.deviation = "Deviation must be positive number"
      if (!ignoreSlippage && Number(slippage) <= 0)
        newErrors.slippage = "Slippage must be positive number"
      if (Number(investmentAmount) <= 0)
        newErrors.investment = "Investment amount must be positive number"

      // Check if any asset has weight <= 0
      const invalidWeights = Object.entries(portfolioTokenWeights).filter(
        ([_, weight]) => Number(weight) < 0.01,
      )
      if (invalidWeights.length > 0) {
        newErrors.weights = "No asset allocation should be smaller than 0.01%."
      }

      const totalAllocated = Object.values(portfolioTokenWeights).reduce(
        (sum, weight) => sum + Number(weight || 0),
        0,
      )
      if (Math.abs(totalAllocated - 100) > 0.01)
        newErrors.weights = `Total allocation must be 100%, not ${totalAllocated.toFixed(2)}%.`

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return
      }

      setErrors({})
      setLoading(true)

      const data: PortfolioAgentParams = {
        agentName: agentName,
        quoteToken: quoteToken as TokenInfo,
        amount: parseNumberAsBigInt(investmentAmount, quoteToken?.denomination),
        // convert deviation to basis points
        deviation: (parseFloat(deviation) * 100).toFixed(0),
        slippage: ignoreSlippage ? undefined : (parseFloat(slippage) * 100).toFixed(0),
        portfolioTokens: portfolioTokens as TokenInfo[],
        pools: ((): Record<string, string> => {
          const mapping = pools.reduce(
            (acc, pool) => {
              if (pool.quoteToken !== quoteTokenId && pool.baseToken !== quoteTokenId) return acc

              const otherToken = pool.quoteToken === quoteTokenId ? pool.baseToken : pool.quoteToken

              if (tokenLiquiditiesPool[otherToken]) {
                acc[otherToken] = {
                  id: tokenLiquiditiesPool[otherToken].pool,
                  liquidity: tokenLiquiditiesPool[otherToken].liquidity,
                }
              }

              if (!acc[otherToken] || pool.liquidity_usd > acc[otherToken].liquidity) {
                acc[otherToken] = { id: pool.id, liquidity: pool.liquidity_usd }
              }
              return acc
            },
            {} as Record<string, { id: string; liquidity: number }>,
          )
          return Object.fromEntries(
            Object.entries(mapping).map(([token, data]) => [token, data.id]),
          )
        })(),
        // convert weights to basis points
        weights: Object.fromEntries(
          Object.entries(portfolioTokenWeights).map(([tokenId, weight]) => [
            tokenId,
            (Number.parseFloat(weight) * 100).toFixed(0),
          ]),
        ),
        // quote usd price
        quoteTokenUsdPrice: quoteTokenUsdPrice as number,
      }

      console.log("onAgentSpawning", data)
      try {
        setAgentStage("spawning")
        await spawnPortfolioAgent(data, (agentId) => {
          setAgentStage("top-up")
          setReceipt(agentId)
        })
        setAgentStage("connecting")
        await wait(2000)
        setAgentStage("receiving")
        await wait(2000)
        setAgentStage("finalizing")
        await wait(2000)
      } catch (error: any) {
        setBackendError(error)
      } finally {
        setLoading(false)
        setAgentStage("done")
      }
    },
    [
      quoteTokenId,
      portfolioTokenIds,
      deviation,
      slippage,
      ignoreSlippage,
      investmentAmount,
      portfolioTokenWeights,
    ],
  )

  const handleIgnoreSlippage = useCallback((checked: boolean) => {
    setErrors({})
    setIgnoreSlippage(checked)
    if (checked) setSlippage("")
  }, [])

  useEffect(() => {
    const abortController = new AbortController()

    ;(async () => {
      try {
        const newErrors: Record<string, string> = {}
        if (!quoteTokenId || !quoteToken) newErrors.quoteToken = "Quote token is required"
        if (portfolioTokenIds.length < 2)
          newErrors.portfolioTokens = "Select at least two portfolio tokens"
        if (Number(deviation) <= 0) newErrors.deviation = "Deviation must be positive number"
        if (!ignoreSlippage && Number(slippage) <= 0)
          newErrors.slippage = "Slippage must be positive number"
        if (Number(investmentAmount) <= 0)
          newErrors.investment = "Investment amount must be positive number"

        // Check if any asset has weight <= 0
        const invalidWeights = Object.entries(portfolioTokenWeights).filter(
          ([_, weight]) => Number(weight) < 0.01,
        )
        if (invalidWeights.length > 0) {
          newErrors.weights = "No asset allocation should be smaller than 0.01%."
        }

        const totalAllocated = Object.values(portfolioTokenWeights).reduce(
          (sum, weight) => sum + Number(weight || 0),
          0,
        )
        if (Math.abs(totalAllocated - 100) > 0.01)
          newErrors.weights = `Total allocation must be 100%, not ${totalAllocated.toFixed(2)}%.`

        if (Object.keys(newErrors).length > 0 || ignoreSlippage) {
          console.log("skippingLiquidityCheck", newErrors)
          setIsLiquidityAcceptable(true)
          return
        }

        const data: PortfolioAgentParams = {
          agentName: agentName,
          quoteToken: quoteToken as TokenInfo,
          amount: parseNumberAsBigInt(investmentAmount, quoteToken?.denomination),
          // convert deviation to basis points
          deviation: (parseFloat(deviation) * 100).toFixed(0),
          slippage: ignoreSlippage ? undefined : (parseFloat(slippage) * 100).toFixed(0),
          portfolioTokens: portfolioTokens as TokenInfo[],
          pools: ((): Record<string, string> => {
            const mapping = pools.reduce(
              (acc, pool) => {
                if (pool.quoteToken !== quoteTokenId && pool.baseToken !== quoteTokenId) return acc

                const otherToken =
                  pool.quoteToken === quoteTokenId ? pool.baseToken : pool.quoteToken

                if (tokenLiquiditiesPool[otherToken]) {
                  acc[otherToken] = {
                    id: tokenLiquiditiesPool[otherToken].pool,
                    liquidity: tokenLiquiditiesPool[otherToken].liquidity,
                  }
                }

                if (!acc[otherToken] || pool.liquidity_usd > acc[otherToken].liquidity) {
                  acc[otherToken] = { id: pool.id, liquidity: pool.liquidity_usd }
                }
                return acc
              },
              {} as Record<string, { id: string; liquidity: number }>,
            )
            return Object.fromEntries(
              Object.entries(mapping).map(([token, data]) => [token, data.id]),
            )
          })(),
          // convert weights to basis points
          weights: Object.fromEntries(
            Object.entries(portfolioTokenWeights).map(([tokenId, weight]) => [
              tokenId,
              (Number.parseFloat(weight) * 100).toFixed(0),
            ]),
          ),
          quoteTokenUsdPrice: quoteTokenUsdPrice as number,
        }

        console.log("onLiquidityCheck", data)
        setCheckingLiquidity(true)
        setRecommendedSlippage(0)
        setHighlightedAsset("")
        const [isAcceptable, recommendedSlippage, assetId] =
          await checkSlippageOnFirstRebalance(data)
        console.log("isAcceptable", isAcceptable, recommendedSlippage)
        if (abortController.signal.aborted) return
        setIsLiquidityAcceptable(isAcceptable)
        if (!isAcceptable) {
          setRecommendedSlippage(recommendedSlippage)
          setHighlightedAsset(assetId)
        }
      } catch (error: any) {
        console.error(error)
        setBackendError(error)
      }

      setCheckingLiquidity(false)
    })()

    return () => {
      setCheckingLiquidity(false)
      abortController.abort()
    }
  }, [
    quoteTokenId,
    portfolioTokenIds,
    deviation,
    slippage,
    investmentAmount,
    portfolioTokenWeights,
    ignoreSlippage,
  ])

  // Prepare data for the pie chart
  const pieData = useMemo(() => {
    if (!portfolioTokens || portfolioTokens.length === 0) return []

    const data = portfolioTokens.map((token) => ({
      name: token.ticker,
      value: Number(portfolioTokenWeights[token.id] || 0),
    }))

    const totalAllocated = data.reduce((sum, item) => sum + item.value, 0)

    if (totalAllocated < 100) {
      data.push({
        name: "Unallocated",
        value: 100 - totalAllocated,
      })
    }

    return data
  }, [portfolioTokens, portfolioTokenWeights])

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AA336A", "#33AA66", "#AA6633"]
  const UNALLOCATED_COLOR = "#888888"

  const { data: quoteTokenUsdPrice } = useHopperPrice(quoteTokenId, "usd")

  useEffect(() => {
    if (props.open && receipt) return

    if (props.open && prefill) {
      try {
        setQuoteTokenId(prefill["Quote-Token"])
        setPortfolioTokenIds(prefill["PortfolioTokens"].map((t) => t.token))
        setDeviation(String(Number(prefill["Deviation-Threshold"]) / 100)) // Convert from basis points to percentage
        const slippageValue = prefill["Slippage-Tolerance"]
          ? String(Number(prefill["Slippage-Tolerance"]) / 100)
          : ""
        setSlippage(slippageValue)
        setIgnoreSlippage(prefill["Slippage-Tolerance"] === "n/A")
        const weights = prefill["PortfolioTokens"].reduce(
          (acc, { token, weight }) => {
            acc[token] = (Number(weight) / 100).toFixed(2)
            return acc
          },
          {} as Record<string, string>,
        )
        setPortfolioTokenWeights(weights)
      } catch {}

      setWeightsPristine(false)
      setReceipt(null)
      setBackendError(null)
      setErrors({})
      setLoading(false)
      setAgentStage("spawning")
      setIsLiquidityAcceptable(true)
      setRecommendedSlippage(0)
      setHighlightedAsset("")
    } else {
      setReceipt(null)
      setBackendError(null)
      setErrors({})
      setQuoteTokenId(PAYMENT_TOKENS[PAYMENT_TOKENS.length - 1].value)
      setPortfolioTokenIds([])
      setDeviation("10")
      setSlippage("")
      setIgnoreSlippage(true)
      setAgentName("")
      setInvestmentAmount("")
      setPortfolioTokenWeights({})
      setWeightsPristine(true)
      setLoading(false)
      setAgentStage("spawning")
      setIsLiquidityAcceptable(true)
      setRecommendedSlippage(0)
      setHighlightedAsset("")
    }
  }, [props.open])

  const isPrefilled = !!prefill

  useEffect(() => {
    setBackendError(null)
  }, [validationError])

  useEffect(() => {
    setIsLiquidityAcceptable(true)
  }, [slippage, investmentAmount, ignoreSlippage])

  const [selectOpen, setSelectOpen] = useState(false)

  const handleFistDeposit = async () => {
    if (!quoteTokenId || !quoteToken || !receipt || !activeAddress) return

    setLoading(true)
    const depositAmountData = {
      quoteTokenId: quoteTokenId,
      amount: parseNumberAsBigInt(investmentAmount, quoteToken?.denomination),
      agentId: receipt,
      quoteTokenUsdPrice: quoteTokenUsdPrice as number,
    }

    try {
      setAgentStage("top-up")
      await deposit(depositAmountData)
      setAgentStage("connecting")
      await wait(2000)
      setAgentStage("receiving")
      await wait(2000)
      setAgentStage("finalizing")
      await wait(2000)
      setAgentStage("done")
      setBackendError(null)
      $hotAgentsMap.setKey(receipt, new Date().getTime().toString())
      $hotOwnersMap.setKey(activeAddress, new Date().getTime().toString())
    } catch (error: any) {
      setBackendError(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      position="top"
      {...rest}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "unset",
          width: 840,
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              "& > svg": {
                animation: "spin 1.5s linear infinite",
                "@keyframes spin": {
                  from: {
                    transform: "rotate(0deg)",
                  },
                  to: {
                    transform: "rotate(360deg)",
                  },
                },
              },
            }}
          >
            <PortfolioAgentIcon />
            <Stack
              sx={{
                paddingLeft: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  textTransform: "uppercase",
                  fontSize: "2rem",
                  color: "rgb(241, 121, 221)",
                }}
                fontFamily={NumberFont}
                fontWeight={700}
              >
                {prefill ? "Clone the trade Strategy of this Agent" : "Create Your Portfolio Agent"}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  textTransform: "uppercase",
                  fontSize: "0.8rem",
                }}
                fontFamily={NumberFont}
                fontWeight={300}
              >
                Automate your portfolio and let your agent work for you.
              </Typography>
            </Stack>
          </Stack>
          <IconButton
            onClick={(event) => {
              props.onClose?.(event, "backdropClick")
            }}
            edge="end"
          >
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
      </DialogTitle>
      {loading ? (
        <>
          <Stack alignItems="center" sx={{ transform: "scale(0.6)" }}>
            <CreateAgentAnimation stage={agentStage} />
          </Stack>
        </>
      ) : receipt ? (
        <DialogContent>
          <Stack alignItems="center" sx={{ transform: "scale(0.6)", marginBottom: "-50px" }}>
            <CreateAgentAnimation stage={agentStage} />
          </Stack>
          <Stack gap={2} alignItems="center">
            {backendError === null ? (
              <>
                <Check
                  color={"accent" as any}
                  sx={{
                    fontSize: 50,
                    padding: 2,
                    background: "rgba(var(--mui-palette-accent-mainChannel) / 0.1)",
                    borderRadius: 50,
                  }}
                />
                <span>Success</span>
              </>
            ) : (
              <>
                <WarningAmberRounded
                  color={"warning" as any}
                  sx={{
                    fontSize: 50,
                    padding: 2,
                    background: "rgba(var(--mui-palette-warning-mainChannel) / 0.1)",
                    borderRadius: 50,
                  }}
                />
                <span>Warning</span>
              </>
            )}
            {String(backendError).toLowerCase().includes("user cancelled") && (
              <>
                <Alert severity="warning" sx={{ width: 600 }}>
                  The Portfolio Agent has been created, but the initial deposit transaction was not
                  approved. To finish the setup click on Deposit.
                </Alert>
                <ShineBorder color="rgba(90, 26, 226, 1)" bgColor="var(--mui-palette-paperAlt)">
                  <Button
                    onClick={handleFistDeposit}
                    variant={"text"}
                    color="inherit"
                    fullWidth
                    sx={{
                      height: 40,
                      width: 100,
                      textTransform: "none",
                      color: "rgb(81, 0, 255)",
                    }}
                  >
                    <Typography variant="body2" component="span" color="inherit" fontWeight={700}>
                      Deposit {formatNumberAuto(investmentAmount)} {quoteToken?.ticker}
                    </Typography>
                  </Button>
                </ShineBorder>
              </>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ResponsiveContainer width={"100%"} height={170}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={50}
                      animationDuration={333}
                      animationBegin={20}
                      label={({ name }) => name}
                      stroke="none"
                    >
                      {pieData.map((entry, index) => {
                        const isUnallocated = entry.name === "Unallocated"
                        const color = isUnallocated
                          ? UNALLOCATED_COLOR
                          : COLORS[index % COLORS.length]
                        return <Cell key={`cell-${index}`} fill={color} />
                      })}
                    </Pie>
                    <Tooltip
                      contentStyle={{ fontSize: "0.75rem" }}
                      separator=": "
                      formatter={(value) => `${Number(value).toFixed(2)}%`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={6}>
                <Stack justifyContent={"space-between"} gap={1}>
                  {agentName && (
                    <Stack>
                      <Typography fontWeight={600}>Agent Name</Typography>
                      <Typography>{agentName}</Typography>
                    </Stack>
                  )}

                  <Stack>
                    <Typography fontWeight={600}>Agent Id</Typography>
                    <IdBlock label={truncateId(receipt)} value={receipt} />
                  </Stack>

                  <Stack>
                    <Typography fontWeight={600}>Investment Amount</Typography>
                    <Typography>
                      {formatNumberAuto(investmentAmount)} {quoteToken?.ticker} (≈ $
                      {quoteTokenUsdPrice
                        ? formatNumberAuto(quoteTokenUsdPrice * Number(investmentAmount), 2)
                        : ""}
                      )
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" alignItems={"center"} gap={2} marginTop={4}>
              <Button variant="outlined">
                <IdBlock
                  label={"View on ao.link"}
                  value={receipt}
                  href={`https://ao.link/#/message/${receipt}`}
                  hideCopyToClipboard
                />
              </Button>
              <ShineBorder
                color="var(--mui-palette-accent-main)"
                bgColor="var(--mui-palette-background-paper)"
              >
                <Button
                  to={`/entity/${receipt}`}
                  sx={{
                    background: "",
                  }}
                  component={Link}
                  onClick={(event) => {
                    event.stopPropagation()
                  }}
                >
                  View on Agent Page
                </Button>
              </ShineBorder>
            </Stack>
          </Stack>
          {backendError && <Alert severity="error">{String(backendError)}</Alert>}
        </DialogContent>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{
              maxHeight: "calc(100vh - 350px)",
              overflowY: "auto",
            }}
          >
            <Stack gap={2}>
              <div>
                <Typography>Agent Name</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  margin="dense"
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </div>
              <div>
                <Typography>Quote Token</Typography>
                <FormControl margin="dense" fullWidth>
                  <Select
                    disabled={isPrefilled}
                    size="medium"
                    value={quoteTokenId}
                    onChange={(e) => {
                      setQuoteTokenId(e.target.value as string)
                      setPortfolioTokenIds([])
                    }}
                    error={!!errors.quoteToken}
                    renderValue={(selected) => <RenderedOption key={selected} tokenId={selected} />}
                  >
                    {PAYMENT_TOKENS.map((token) => (
                      <SelectOption
                        key={token.value}
                        value={token.value}
                        tokenId={token.value}
                        disabled={token.disabled}
                      />
                    ))}
                  </Select>
                </FormControl>
              </div>
              <Stack gap={1}>
                <div>
                  <Typography>Portfolio Tokens</Typography>
                  <FormControl fullWidth margin="dense">
                    <Select
                      disabled={isPrefilled}
                      open={selectOpen}
                      onOpen={() => {
                        setSelectOpen(true)
                      }}
                      onClose={() => {
                        setSelectOpen(false)
                      }}
                      size="medium"
                      multiple
                      labelId="base-token-input-label"
                      value={portfolioTokenIds}
                      onChange={(e) => {
                        setPortfolioTokenIds(e.target.value as string[])
                        setWeightsPristine(true)
                      }}
                      renderValue={() => portfolioTokens?.map((x) => x.ticker).join(", ")}
                      error={!!errors.portfolioTokens}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: "var(--mui-palette-background-default)",
                            border: 0,
                          },
                        },
                        MenuListProps: {
                          sx: {
                            paddingX: 2.5,
                            paddingY: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          },
                        },
                      }}
                    >
                      <Box onKeyDown={(e) => e.stopPropagation()} paddingBottom={2}>
                        <TextField
                          fullWidth
                          autoFocus
                          placeholder="Search by name, ticker or address"
                          value={searchInput}
                          onChange={(e) => {
                            setSearchInput(e.target.value)
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        />
                      </Box>
                      {filteredTokenIdOptions?.map(({ id: tokenId }) => (
                        <SelectOptionExtra
                          key={tokenId}
                          value={tokenId}
                          tokenId={tokenId}
                          onLiquidityFound={handleLiquidityFound}
                          pools={
                            pools.filter(
                              (pool) =>
                                (pool.baseToken === tokenId && pool.quoteToken === quoteTokenId) ||
                                (pool.baseToken === quoteTokenId && pool.quoteToken === tokenId),
                            ) as Pool[]
                          }
                        />
                      ))}

                      {!filteredTokenIdOptions?.length && (
                        <Typography variant="body2" sx={{ padding: 2 }}>
                          No tokens found
                        </Typography>
                      )}

                      {portfolioTokenIds.length > 1 && (
                        <Box onKeyDown={(e) => e.stopPropagation()} paddingTop={2}>
                          <Button
                            onClick={() => {
                              setSelectOpen(false)
                            }}
                            variant="outlined"
                            fullWidth
                            color="secondary"
                            sx={{
                              height: 56,
                              background: "rgba(var(--mui-palette-secondary-mainChannel) / 0.1)",
                              textTransform: "none",
                            }}
                          >
                            <Typography
                              variant="h5"
                              component="span"
                              color="secondary.main"
                              fontWeight={700}
                            >
                              Confirm Selection
                            </Typography>
                          </Button>
                        </Box>
                      )}
                    </Select>
                    <FormHelperText error={!!errors.portfolioTokens}>
                      {errors.portfolioTokens}
                    </FormHelperText>
                  </FormControl>
                </div>
                <Paper variant="outlined">
                  <Stack
                    alignItems="center"
                    sx={{
                      "& .recharts-text.recharts-pie-label-text": {
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      },
                    }}
                  >
                    {pieData && pieData.length > 0 && (
                      <ResponsiveContainer width={"100%"} height={200}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={50}
                            animationDuration={333}
                            animationBegin={20}
                            label={({ name }) => name}
                            stroke="none"
                          >
                            {pieData.map((entry, index) => {
                              const isUnallocated = entry.name === "Unallocated"
                              const color = isUnallocated
                                ? UNALLOCATED_COLOR
                                : COLORS[index % COLORS.length]
                              return <Cell key={`cell-${index}`} fill={color} />
                            })}
                          </Pie>
                          <Tooltip
                            contentStyle={{ fontSize: "0.75rem" }}
                            separator=": "
                            formatter={(value) => `${Number(value).toFixed(2)}%`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}{" "}
                  </Stack>
                  {portfolioTokenIds.length > 1 && (
                    <Stack sx={{ paddingX: 3, paddingBottom: 2 }} gap={0.5}>
                      {portfolioTokenIds.map((baseTokenId) => (
                        <Stack
                          key={baseTokenId}
                          direction="row"
                          gap={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" gap={0.5} alignItems="center">
                            <RenderedOption tokenId={baseTokenId} />
                            {!ignoreSlippage && highlightedAsset === baseTokenId && (
                              <MuiTooltip title="This asset has the lowest liquidity of the selected tokens.">
                                <WarningAmber color={"warning" as any} />
                              </MuiTooltip>
                            )}
                          </Stack>
                          <TextField
                            disabled={isPrefilled}
                            size="small"
                            sx={{ width: 160 }}
                            type="number"
                            value={portfolioTokenWeights[baseTokenId] || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              setPortfolioTokenWeights((prevWeights) => ({
                                ...prevWeights,
                                [baseTokenId]: value,
                              }))
                              setWeightsPristine(false)
                            }}
                            error={!!errors.weights}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Stack>
                      ))}
                      {errors.weights && (
                        <FormHelperText error={!!errors.weights}>{errors.weights}</FormHelperText>
                      )}
                    </Stack>
                  )}
                </Paper>
              </Stack>
              <div>
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <Typography>Deviation Percentage</Typography>
                  <MuiTooltip title="Sets how much your agent can stray from the target allocation during rebalancing. A 10% deviation is reasonable—for example, in a 4-asset portfolio, each asset may shift by up to 2.5%, while in a 10-asset portfolio, each moves about 1%.">
                    <InfoOutlined fontSize="small" />
                  </MuiTooltip>
                </Stack>
                <TextField
                  disabled={isPrefilled}
                  fullWidth
                  size="medium"
                  margin="dense"
                  type="number"
                  value={deviation}
                  onChange={(e) => setDeviation(e.target.value)}
                  error={!!errors.deviation}
                  helperText={errors.deviation}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 10, step: 1, max: 300 },
                  }}
                />
              </div>
              <div>
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <Typography>Max Slippage</Typography>
                  <MuiTooltip title="Defines the maximum price impact when your agent swaps tokens. Since we primarily support Uniswap V2 type pools right now, some price impact is unavoidable. A minimum of 5% slippage tolerance is recommended to accommodate low-liquidity pools. The agent will always seek the best execution with the lowest impact before proceeding.">
                    <InfoOutlined fontSize="small" />
                  </MuiTooltip>
                </Stack>
                <Stack direction={"row"} gap={2} alignItems={"flex-start"}>
                  <TextField
                    fullWidth
                    size="medium"
                    margin="dense"
                    disabled={ignoreSlippage}
                    placeholder={ignoreSlippage ? "N/A" : ""}
                    type="number"
                    value={slippage}
                    onChange={(e) => setSlippage(e.target.value)}
                    error={!!errors.slippage}
                    helperText={
                      errors.slippage || "Recommended: minimum 8% for lower liquidity pools"
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 5, step: 1, max: 99 },
                    }}
                  />
                  <FormControlLabel
                    sx={{ width: 280, mt: 2 }}
                    control={
                      <Checkbox
                        checked={ignoreSlippage}
                        onChange={(e) => handleIgnoreSlippage(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                        <Typography>Flexible Slippage</Typography>
                        <MuiTooltip title="Recommended for continuous trading—uncheck to restrict trades.">
                          <InfoOutlined fontSize="small" />
                        </MuiTooltip>
                      </Stack>
                    }
                  />
                </Stack>
              </div>
              <div>
                <Stack
                  justifyContent="space-between"
                  direction={"row"}
                  alignItems={"center"}
                  gap={0.5}
                >
                  <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                    <Typography>Investment Amount</Typography>
                    <MuiTooltip title="Enter how much of your balance you want to allocate to this portfolio agent. You can choose a specific amount or use the quick buttons.">
                      <InfoOutlined fontSize="small" />
                    </MuiTooltip>
                  </Stack>
                  {typeof balance === "bigint" && (
                    <ButtonGroup size="small">
                      <Button
                        onClick={() => {
                          setInvestmentAmount(
                            parseBigIntAsNumber(
                              balance / 4n,
                              quoteToken?.denomination,
                              INPUT_DECIMALS,
                            ),
                          )
                        }}
                      >
                        25%
                      </Button>
                      <Button
                        onClick={() => {
                          setInvestmentAmount(
                            parseBigIntAsNumber(
                              balance / 2n,
                              quoteToken?.denomination,
                              INPUT_DECIMALS,
                            ),
                          )
                        }}
                      >
                        50%
                      </Button>
                      <Button
                        onClick={() => {
                          setInvestmentAmount(
                            parseBigIntAsNumber(
                              (balance * 2n) / 3n,
                              quoteToken?.denomination,
                              INPUT_DECIMALS,
                            ),
                          )
                        }}
                      >
                        75%
                      </Button>
                      <Button
                        onClick={() => {
                          setInvestmentAmount(
                            parseBigIntAsNumber(balance, quoteToken?.denomination),
                          )
                        }}
                      >
                        MAX
                      </Button>
                    </ButtonGroup>
                  )}
                </Stack>
                <TextField
                  fullWidth
                  size="medium"
                  margin="dense"
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  error={!!errors.investment}
                  helperText={
                    <Stack direction="row" justifyContent={"space-between"} gap={1}>
                      <div>
                        {errors.investment
                          ? errors.investment
                          : investmentAmount
                            ? `≈ $${quoteTokenUsdPrice ? formatNumberAuto(quoteTokenUsdPrice * Number(investmentAmount), 2) : ""}`
                            : ""}
                      </div>
                      <div>
                        Balance:{" "}
                        {formatNumberAuto(parseBigIntAsNumber(balance, quoteToken?.denomination))}
                      </div>
                    </Stack>
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{quoteToken?.ticker}</InputAdornment>
                    ),
                    inputProps: {
                      // min: 0,
                      // max: parseBigIntAsNumber(balance, quoteToken?.denomination),
                    },
                  }}
                />
              </div>
            </Stack>
            {backendError && <Alert severity="error">{String(backendError)}</Alert>}
          </DialogContent>

          <DialogActions sx={{ paddingX: 3 }}>
            {activeAddress ? (
              <>
                {!isLiquidityAcceptable ? (
                  <Stack gap={1}>
                    <Alert severity="warning">
                      Some of your selected pools may have lower liquidity, which could lead to
                      slightly higher slippage.
                    </Alert>
                    <Stack direction="row" gap={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{
                          textTransform: "none",
                        }}
                        fullWidth
                        onClick={() => {
                          setSlippage(String(Math.round(recommendedSlippage / 100)))
                        }}
                      >
                        Increase Max Slippage to {Math.round(recommendedSlippage / 100)}%
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        sx={{
                          textTransform: "none",
                        }}
                        onClick={() => {
                          setSlippage("")
                          setIgnoreSlippage(true)
                        }}
                      >
                        Use Flexible Slippage
                      </Button>
                    </Stack>
                  </Stack>
                ) : loading || checkingLiquidity || !!validationError ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    color="accent"
                    sx={{
                      height: 56,
                      background: validationError
                        ? "rgba(0, 0, 0, 0.1)"
                        : "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
                      textTransform: "none",
                      "& img": {
                        width: 47,
                        height: 42,
                        marginRight: 1,
                      },
                      "&.Mui-disabled": {
                        background: "rgba(0, 0, 0, 0.1)",
                        color: "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                    type="submit"
                    disabled={loading || checkingLiquidity || !!validationError}
                  >
                    {validationError ? null : checkingLiquidity ? (
                      <CircularProgress size={20} color="secondary" sx={{ mr: 4.25 }} />
                    ) : (
                      <img src="/dexi_agent_v2.png" alt="Spawn Agent" />
                    )}
                    <Typography
                      variant="h6"
                      component="span"
                      color={
                        loading || checkingLiquidity || !!validationError
                          ? "text.disabled"
                          : "success.main"
                      }
                    >
                      {validationError ||
                        (checkingLiquidity ? "Simulating first rebalance" : "Spawn Agent")}
                    </Typography>
                  </Button>
                ) : (
                  <Box sx={{ width: "100%", "& > div": { width: "100% !important" } }}>
                    <ShineBorder
                      color={"var(--mui-palette-accent-main)"}
                      bgColor="var(--mui-palette-background-default)"
                    >
                      <Button
                        variant="text"
                        fullWidth
                        color="accent"
                        sx={{
                          background: "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
                          textTransform: "none",
                          "& img": {
                            width: 47,
                            height: 42,
                            marginRight: 1,
                          },
                        }}
                        type="submit"
                      >
                        <img src="/dexi_agent_v2.png" alt="Spawn Agent" />
                        <Typography variant="h6" component="span" color="accent.main">
                          Spawn Agent
                        </Typography>
                      </Button>
                    </ShineBorder>
                  </Box>
                )}
              </>
            ) : (
              <Button
                onClick={connect}
                variant="outlined"
                fullWidth
                color="accent"
                sx={{
                  height: 56,
                  background: "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
                  textTransform: "none",
                }}
              >
                <Typography variant="h5" component="span" color="accent.main" fontWeight={700}>
                  Connect Wallet
                </Typography>
              </Button>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  )
}

function SelectOption(props: { tokenId: string } & MenuItemProps) {
  const { tokenId, ...rest } = props

  return (
    <MenuItem {...rest}>
      <RenderedOption tokenId={tokenId} />
    </MenuItem>
  )
}

function SelectOptionExtra(
  props: {
    tokenId: string
    pools: Pool[]
    onLiquidityFound?: (tokenId: string, liquidity: number, pool?: string) => void
  } & MenuItemProps,
) {
  const { tokenId, pools, onLiquidityFound, ...rest } = props

  const { bestPool: pool, liquidity: bestPoolLiquidity } = useBestPool(pools, tokenId)

  const [baseToken] = useTokenInfo(tokenId)

  const { data: tokenUsdPrice } = useHopperPrice(tokenId, "usd")

  const liquidity =
    !pool || !tokenUsdPrice
      ? 0
      : Number(parseBigIntAsNumber(bestPoolLiquidity, baseToken?.denomination)) * tokenUsdPrice

  useEffect(() => {
    onLiquidityFound?.(tokenId, liquidity, pool?.id)
  }, [tokenId, liquidity, pool?.id, onLiquidityFound])

  if (!pool) return null

  return (
    <MenuItem
      {...rest}
      sx={{
        display: "grid",
        gridTemplateColumns: "1.1fr 0.9fr 24px",
        gap: 1,
        flex: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <TokenAvatar tokenId={tokenId} size="large" />
        <Stack>
          <Typography component="div" variant="inherit" fontWeight={700}>
            {baseToken?.name}
          </Typography>
          <Typography component={Stack} variant="body2" direction="row" gap={0.5}>
            {baseToken?.ticker} •
            <Typography variant="caption">
              <IdBlock
                label={"View Token"}
                value={tokenId}
                href={`/token/${tokenId}`}
                target="_blank"
                hideCopyToClipboard
              />
            </Typography>
          </Typography>
        </Stack>
      </Stack>
      <Stack justifyContent="center">
        <Typography component="div" variant="body2">
          Token price:{" "}
          {baseToken ? (
            <TokenAmountInCurrency
              amount={parseUnits("1", baseToken.denomination)}
              tokenId={tokenId}
              hideCopyToClipboard
              quoteTokenUsdPrice={tokenUsdPrice}
            />
          ) : (
            <Skeleton width={60} sx={{ display: "inline-block" }} />
          )}
        </Typography>
        <Typography component="div" variant="body2">
          Liquidity:{" "}
          {bestPoolLiquidity ? (
            <TokenAmountInCurrency
              amount={bestPoolLiquidity}
              needsParsing
              quoteTokenUsdPrice={tokenUsdPrice}
              tokenId={tokenId}
            />
          ) : (
            <Skeleton width={60} sx={{ display: "inline-block" }} />
          )}
        </Typography>
      </Stack>
      {rest.selected && (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            borderRadius: 0.5,
            height: 22,
            width: 22,
            background: "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
            border: "1px solid",
            borderColor: "var(--mui-palette-success-main)",
            "& svg": {
              width: "0.75rem",
              height: "0.75rem",
            },
          }}
        >
          <Check style={{ color: "var(--mui-palette-accent-main)" }} />
        </Stack>
      )}
    </MenuItem>
  )
}

function RenderedOption(props: { tokenId: string }) {
  const { tokenId } = props

  const [tokenInfo] = useTokenInfo(tokenId)

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexGrow={1}>
      <TokenAvatar tokenId={tokenId} />
      <Typography component="div" variant="inherit" fontWeight={500}>
        {tokenInfo?.ticker}
      </Typography>
    </Stack>
  )
}
